import React, { useState } from 'react';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  Link,
  Grid,
  Fade
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import '../styles/Loader.css';

const FarmerLogin = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
    showPassword: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleTogglePasswordVisibility = () => {
    setFormData({
      ...formData,
      showPassword: !formData.showPassword
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    try {
      setIsLoading(true);
      // Connexion avec Firebase Authentication
      const userCredential = await signInWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      const user = userCredential.user;
      
      // LOGIQUE STRICTE: Vérifier si l'utilisateur est UNIQUEMENT un agriculteur (vendeur)
      const farmerDoc = await getDoc(doc(db, "farmers", user.uid));
      
      if (!farmerDoc.exists()) {
        // Vérifier si c'est un buyer qui essaie de se connecter comme farmer
        const buyerDoc = await getDoc(doc(db, "buyers", user.uid));
        
        if (buyerDoc.exists()) {
          await signOut(auth);
          setIsLoading(false);
          setError("Ce compte est enregistré comme acheteur. Vous ne pouvez pas vous connecter en tant que vendeur. Veuillez utiliser la connexion acheteur.");
          return;
        } else {
          await signOut(auth);
          setIsLoading(false);
          setError("Ce compte n'est pas enregistré comme vendeur dans notre système.");
          return;
        }
      }
      
      const userData = farmerDoc.data();
      
      // Vérifier si l'email a été vérifié directement depuis l'objet Auth
      if (!user.emailVerified) {
        await signOut(auth);
        setIsLoading(false);
        setError("Veuillez vérifier votre email avant de vous connecter");
        return;
      }
      
      // Mettre à jour le champ emailVerified dans Firestore si nécessaire
      if (!userData.emailVerified && user.emailVerified) {
        await setDoc(doc(db, "farmers", user.uid), {
          ...userData,
          emailVerified: true,
          updatedAt: new Date()
        }, { merge: true });
      }
      
      // Vérifier si le compte a été approuvé par un administrateur
      if (!userData.adminApproved) {
        await signOut(auth);
        setIsLoading(false);
        setError("Votre compte vendeur est en attente de validation par un administrateur");
        return;
      }
      
      setIsLoading(false);
      // Rediriger vers le tableau de bord des vendeurs
      navigate('/farmer-dashboard');
      
    } catch (error) {
      setIsLoading(false);
      let errorMessage = 'Une erreur est survenue lors de la connexion';
      
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = 'Email ou mot de passe incorrect';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Trop de tentatives échouées. Veuillez réessayer plus tard';
      }
      
      setError(errorMessage);
      console.error("Erreur de connexion:", error);
    }
  };

  return (
    <div className="form-container">
      <Fade in={true} timeout={500}>
        <Box sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.9)', 
          borderRadius: '10px',
          p: 3,
          boxShadow: 3,
          maxWidth: '450px',
          mx: 'auto'
        }}>
          <Typography variant="h4" component="h1" align="center" gutterBottom sx={{ 
            color: '#014527',
            fontWeight: 'bold',
            position: 'relative',
            pb: 2,
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '4px',
              backgroundColor: '#28a745',
              borderRadius: '2px'
            }
          }}>
            Connexion
          </Typography>
          
          {error && (
            <Box sx={{ 
              backgroundColor: 'rgba(255, 0, 0, 0.1)', 
              color: 'error.main',
              p: 2,
              borderRadius: 1,
              mb: 3
            }}>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
          
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email color="primary" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mot de passe"
                  name="password"
                  type={formData.showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="primary" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {formData.showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.rememberMe}
                    onChange={handleChange}
                    name="rememberMe"
                    color="primary"
                  />
                }
                label="Se souvenir de moi"
              />
              
              <Link 
                component="button"
                variant="body2"
                onClick={() => navigate('/reset-password')}
                sx={{ color: '#0066cc' }}
              >
                Mot de passe oublié?
              </Link>
            </Box>
            
            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              disabled={isLoading}
              sx={{ 
                bgcolor: '#014527',
                '&:hover': { bgcolor: '#035417' },
                py: 1.5,
                mb: 3
              }}
            >
              {isLoading ? <div className="loader"></div> : "Se connecter"}
            </Button>
            
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2">
                Pas encore de compte?{' '}
                <Link 
                  component="button"
                  variant="body2"
                  onClick={() => navigate('/register')}
                  sx={{ color: '#0066cc', fontWeight: 'medium' }}
                >
                  S'inscrire
                </Link>
              </Typography>
            </Box>
          </form>
          
          {/* Overlay de chargement qui s'affiche sur toute la page */}
          {isLoading && (
            <div className="loader-overlay">
              <div className="loader"></div>
            </div>
          )}
        </Box>
      </Fade>
    </div>
  );
};

export default FarmerLogin;